import React, { Component } from "react";
import ReactDOM from "react-dom";
import StockChart from "./StockChart";
import StockRadarChart from "../main/StockRadarChart";
import Constants from "../util/Constants";
import Utils from "../util/Utils";
import CompanyNews from "./CompanyNews";
import CompanyPage from "./CompanyPage";
import CompanyRecommendations from "./CompanyRecommendations";
import StockGrade from "./StockGrade";
import { isMobile } from "react-device-detect";
import {
  Box,
  Card,
  CardContent,
  Container,
  Typography,
  TableBody,
  TableContainer,
  TableRow,
  TableCell,
  Paper,
  CircularProgress,
  useTheme,
  useMediaQuery,
  alpha,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Button,
  Modal,
  Table,
} from "@mui/material";
import {
  Info as InfoIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  AttachMoney as AttachMoneyIcon,
  ExpandMore as ExpandMoreIcon,
  ShowChart as ShowChartIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  textPrimary:   "#0f1f35",
  textSecondary: "#3d5166",
  textMuted:     "#7a92a8",
  bg:            "#ffffff",
  card:          "#ffffff",
  cardHeader:    "#f5f2ed",       // warm cream/beige, soft and professional
  cardHeaderText: "#1a2744",       // deep navy, clear and readable
  border:        "#d0dcea",
  borderLight:   "#e8eef6",
  navyDark:      "#0f2d52",
  navy:          "#1a4075",
  navyMid:       "#2c5282",
  navyLight:     "#dce8f5",
  success:       "#0a8a7e",
  successBg:     "#e6f9f7",
  danger:        "#b91c1c",
  dangerBg:      "#fde8e8",
  warning:       "#b45309",
};

const MONO = "'DM Mono', 'Fira Mono', 'Courier New', monospace";
const SANS = "'Lato', 'Helvetica Neue', Arial, sans-serif";
const PROFILE_FONT = SANS; // Keep for backward compatibility

// ── Styled components ────────────────────────────────────────────────────────
const StyledCard = styled(Card)(({ theme }) => ({
  fontFamily: SANS,
  height: "100%",
  width: "100%",
  border: `1px solid ${C.border}`,
  borderRadius: "10px",
  boxShadow: "0 1px 3px rgba(15,45,82,0.06)",
  overflow: "hidden",
  transition: "box-shadow 0.15s",
  backgroundColor: "#ffffff",
  marginBottom: theme.spacing(0.5),
  "&:hover": {
    boxShadow: "0 4px 12px rgba(15,45,82,0.1)",
  },
  "& .MuiCardContent-root": {
    padding: theme.spacing(1),
    height: "100%",
    display: "flex",
    flexDirection: "column",
    "&:last-child": {
      paddingBottom: theme.spacing(1),
    },
  },
}));

const ChartCard = styled(StyledCard)(({ theme }) => ({
  minHeight: "260px",
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${C.border}`,
  backgroundColor: theme.palette.background.paper,
}));

const PriceChartCard = styled(StyledCard)(({ theme }) => ({
  minHeight: "300px",
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${C.border}`,
  backgroundColor: theme.palette.background.paper,
}));

const AboutCard = styled(StyledCard)(({ theme }) => ({
  minHeight: "60px",
  height: "auto",
  transition: "height 0.3s ease-in-out",
  "&.expanded": {
    height: "auto",
    minHeight: "60px",
  },
  "& .MuiCardContent-root": {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(0.5, 1),
    "&:last-child": {
      paddingBottom: theme.spacing(0.5),
    },
  },
  "& .MuiAccordion-root": {
    boxShadow: "none",
    "&:before": {
      display: "none",
    },
  },
  "& .MuiAccordionSummary-root": {
    padding: 0,
    minHeight: "unset",
  },
  "& .MuiAccordionSummary-content": {
    margin: 0,
    "& .MuiTypography-root": {
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  },
  "& .MuiAccordionDetails-root": {
    padding: theme.spacing(0.5, 0),
  },
}));

// Table-only cards: minimal padding so tables sit close to card edges
const TableCard = styled(StyledCard)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  "& .MuiCardContent-root": {
    padding: 0,
    flex: 1,
    display: "flex",
    flexDirection: "column",
    "&:last-child": {
      paddingBottom: 0,
    },
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  fontFamily: SANS,
  overflow: "auto",
  width: "100%",
  height: "100%",
  "&::-webkit-scrollbar": {
    width: "0px",
    background: "transparent",
  },
  "& .MuiTableCell-root": {
    padding: theme.spacing(0.35, 1.25),
    fontSize: "0.875rem",
    lineHeight: 1.25,
    fontFamily: SANS,
  },
  "& .MuiTableRow-root:hover": {
    backgroundColor: C.cardHeader,
  },
}));

const NewsCard = styled(StyledCard)(({ theme }) => ({
  minHeight: "320px",
  height: "100%",
  "& .MuiCardContent-root": {
    padding: 0,
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    "&:last-child": { paddingBottom: 0 },
  },
}));

const GradeCard = styled(StyledCard)(({ theme }) => ({
  minHeight: "320px",
  height: "100%",
  "& .MuiCardContent-root": {
    padding: 0,
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    "&:last-child": { paddingBottom: 0 },
  },
}));

export default class CompanyProfile extends Component {
  constructor(props) {
    super(props);
    this.state = {
      open: false,
      aboutExpanded: false,
      yahoo: "https://finance.yahoo.com/quote/" + props.stockValues.symbol,
      marketwatch:
        "https://www.marketwatch.com/investing/stock/" +
        props.stockValues.symbol,
      google:
        "https://www.google.com/finance/quote/" +
        props.stockValues.symbol +
        ":" +
        props.stockValues.compProfile.exchangeShortName,
      finviz: "https://finviz.com/quote.ashx?t=" + props.stockValues.symbol,
      holders:
        "https://finance.yahoo.com/quote/" +
        props.stockValues.symbol +
        "/holders",
      tableHeight: !isMobile ? "590px" : "auto",
      showScrollHint: true,
    };
  }

  componentDidMount() {
    this.handleScroll = () => {
      if (window.scrollY > 200 && this.state.showScrollHint) {
        this.setState({ showScrollHint: false });
      }
    };
    window.addEventListener("scroll", this.handleScroll, { passive: true });
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
  }

  setOpen = (flag) => {
    this.setState({
      open: flag,
    });
  };

  // Threshold-based color for metrics: higher is better (normal) or lower is better (inverse)
  getValueColor = (row) => {
    const { value, threshold = 0, inverse = false } = row;
    if (value === undefined || value === null) return C.textSecondary;
    const num = Number(value);
    if (inverse) {
      if (num < threshold) return C.success;
      if (num > threshold * 2) return C.danger;
      return C.warning;
    }
    if (num > threshold) return C.success;
    if (num < 0) return C.danger;
    return C.warning;
  };

  showModal = (symbol) => {
    this.setState({
      modalSymbol: symbol,
      open: true,
    });
  };

  render = () => {
    const { stockValues } = this.props;

    if (!stockValues) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <>
      <Container
        maxWidth="lg"
        disableGutters={false}
        sx={{
          fontFamily: PROFILE_FONT,
          py: { xs: 2, md: 2.5 },
          px: { xs: 1, sm: 1.5, md: 2 },
          mt: 0,
          width: "100%",
          maxWidth: 1280,
          marginLeft: "auto",
          marginRight: "auto",
          overflowX: "hidden",
        }}
      >
        <Grid container spacing={!isMobile ? 1.5 : 1} sx={{ width: "100%", margin: 0, alignItems: "stretch" }}>
          {/* First Row: About Card — preview in summary, remainder only in details to avoid repetition */}
          <Grid item xs={12}>
            <AboutCard className={this.state.aboutExpanded ? "expanded" : ""}>
              <CardContent>
                {(() => {
                  const full = stockValues.compProfile.description || "No description available.";
                  const PREVIEW_LEN = 400;
                  let preview = "";
                  let remainder = "";
                  if (full.length <= PREVIEW_LEN) {
                    preview = full;
                  } else {
                    const chunk = full.slice(0, PREVIEW_LEN);
                    const lastSpace = chunk.lastIndexOf(" ");
                    preview = lastSpace > 100 ? chunk.slice(0, lastSpace) : chunk;
                    remainder = full.slice(preview.length).trim();
                  }
                  return (
                    <Accordion
                      expanded={this.state.aboutExpanded}
                      onChange={(event, expanded) => {
                        this.setState({ aboutExpanded: expanded });
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          padding: 0,
                          minHeight: "unset",
                          "& .MuiAccordionSummary-content": {
                            margin: 0,
                          },
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            fontFamily: PROFILE_FONT,
                            fontSize: "1rem",
                            lineHeight: 1.4,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {preview}{remainder ? " …" : ""}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ padding: 0 }}>
                        <Typography
                          variant="body1"
                          sx={{
                            fontFamily: PROFILE_FONT,
                            fontSize: "1rem",
                            lineHeight: 1.4,
                          }}
                        >
                          {remainder || full}
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  );
                })()}
              </CardContent>
            </AboutCard>
          </Grid>

          {/* Row 2: 4 cards in one row - height fits content, no wasted space */}
          <Grid item xs={12} sx={{ display: "flex", minHeight: 0 }}>
            <Box
              sx={{
                width: "100%",
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" },
                gridTemplateRows: { xs: "auto auto auto auto", md: "auto" },
                gap: !isMobile ? 1.5 : 1,
                alignItems: "start",
              }}
            >
              {/* Stock Analysis - compact so it aligns with other cards in row */}
              <ChartCard sx={{ minHeight: 0, maxHeight: 280, display: "flex", flexDirection: "column", overflow: "hidden", "& .MuiCardContent-root": { p: 0 } }}>
                <CardContent sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", "&:last-child": { pb: 0 } }}>
                  <Box sx={{
                    display: "flex", alignItems: "center", gap: 1.25,
                    px: 1.5, py: 1,
                    borderBottom: `1px solid ${C.borderLight}`,
                    backgroundColor: C.cardHeader,
                    flexShrink: 0,
                  }}>
                    <AssessmentIcon sx={{ fontSize: 20, color: C.navy }} />
                    <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: C.cardHeaderText, fontFamily: SANS }}>
                      Stock Analysis
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      p: 0.5,
                      pt: 1.5,
                      flex: 1,
                      minHeight: 0,
                      maxHeight: 200,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <StockRadarChart
                        scoreData={stockValues.stockReport}
                        scoreRadar={this.props.scoreRadar}
                        width={160}
                        height={160}
                        labelSize="long"
                        innerRadius={0.4}
                        outerRadius={0.8}
                        darkBackground={false}
                        onScoreClick={this.props.onNavigateToBeta}
                      />
                  </Box>
                </CardContent>
              </ChartCard>

              {/* Analyst Recommendations */}
              <ChartCard sx={{ minHeight: 280, height: "auto", display: "flex", flexDirection: "column", overflow: "hidden", "& .MuiCardContent-root": { p: 0 } }}>
                <CardContent sx={{ display: "flex", flexDirection: "column", "&:last-child": { pb: 0 } }}>
                  <Box sx={{
                    display: "flex", alignItems: "center", gap: 1.25,
                    px: 1.5, py: 1,
                    borderBottom: `1px solid ${C.borderLight}`,
                    backgroundColor: C.cardHeader,
                  }}>
                    <TrendingUpIcon sx={{ fontSize: 20, color: C.navy }} />
                    <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: C.cardHeaderText, fontFamily: SANS }}>
                      Analyst Recommendations
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: "100%",
                      height: 200,
                      minHeight: 200,
                      display: "block",
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        width: "100%",
                        height: "100%",
                      }}
                    >
                      <CompanyRecommendations
                        symbol={stockValues.compProfile.symbol}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </ChartCard>

              {/* Company Overview Card */}
              <TableCard sx={{ minHeight: 0, display: "flex", flexDirection: "column" }}>
                  <CardContent sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", p: 0 }}>
                    <Box sx={{
                      display: "flex", alignItems: "center", gap: 1.25,
                      px: 1.5, py: 1,
                      borderBottom: `1px solid ${C.borderLight}`,
                      backgroundColor: C.cardHeader,
                    }}>
                      <Typography sx={{
                        fontWeight: 700, fontSize: "1rem",
                        color: C.cardHeaderText, fontFamily: SANS,
                      }}>
                        Company Overview
                      </Typography>
                    </Box>
                    <StyledTableContainer component={Paper} elevation={0} sx={{ flex: 1, minHeight: 0, overflow: "auto", maxHeight: "none" }}>
                      <Table size="small">
                        <TableBody>
                      {[
                        {
                          label: "Country",
                          value: stockValues.compProfile.country || "N/A",
                        },
                        {
                          label: "CEO",
                          value: stockValues.compProfile.ceo || "N/A",
                        },
                        {
                          label: "Employees",
                          value:
                            Utils.format_number(
                              stockValues.compProfile.fullTimeEmployees
                            ) || "N/A",
                        },
                        {
                          label: "Public Since",
                          value: stockValues.compProfile.ipoDate
                            ? stockValues.compProfile.ipoDate.slice(0, 10)
                            : "N/A",
                        },
                        {
                          label: "Exchange",
                          value: stockValues.compProfile.exchangeShortName,
                        },
                        {
                          label: "Index",
                          value: stockValues.compProfile.index || "N/A",
                        },
                        {
                          label: "Sales (ttm)",
                          value: (() => {
                            const sales = stockValues.compProfile.sales;
                            if (!sales) return "N/A";
                            if (
                              typeof sales === "string" &&
                              sales.endsWith("B")
                            ) {
                              const value =
                                parseFloat(sales.replace("B", "")) * 1000000000;
                              return Utils.format_currency_short(value);
                            }
                            return Utils.format_currency_short(sales) || "N/A";
                          })(),
                        },
                        {
                          label: "Earnings (ttm)",
                          value: (() => {
                            const income = stockValues.compProfile.income;
                            if (!income) return "N/A";
                            if (
                              typeof income === "string" &&
                              income.endsWith("B")
                            ) {
                              const value =
                                parseFloat(income.replace("B", "")) *
                                1000000000;
                              return Utils.format_currency_short(value);
                            }
                            return Utils.format_currency_short(income) || "N/A";
                          })(),
                        },
                        {
                          label: "Earnings Date",
                          value: (() => {
                            if (!stockValues.compProfile.earningsDateString) {
                              return "Not Announced";
                            }
                            const date = new Date(
                              stockValues.compProfile.earningsDateString
                            );
                            return date.toISOString().split("T")[0];
                          })(),
                        },
                      ].map((row, index) => (
                        <TableRow key={index}>
                          <TableCell
                            component="th"
                            scope="row"
                            sx={{ fontWeight: 600, fontSize: "0.875rem", color: C.textSecondary, fontFamily: SANS, px: 1.5 }}
                          >
                            {row.label}
                          </TableCell>
                          <TableCell 
                            align="right"
                            sx={{
                              fontSize: "1rem",
                              fontWeight: 700,
                              color: C.textPrimary,
                              fontFamily: MONO,
                              fontVariantNumeric: "tabular-nums",
                              whiteSpace: "nowrap",
                              px: 1.5,
                            }}
                          >
                            {row.value}
                          </TableCell>
                        </TableRow>
                      ))}
                        </TableBody>
                      </Table>
                    </StyledTableContainer>
                  </CardContent>
                </TableCard>

              {/* Market Information Card */}
              <TableCard sx={{ minHeight: 0, display: "flex", flexDirection: "column" }}>
                  <CardContent sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", p: 0 }}>
                    <Box sx={{
                      display: "flex", alignItems: "center", gap: 1.25,
                      px: 1.5, py: 1,
                      borderBottom: `1px solid ${C.borderLight}`,
                      backgroundColor: C.cardHeader,
                    }}>
                      <Typography sx={{
                        fontWeight: 700, fontSize: "1rem",
                        color: C.cardHeaderText, fontFamily: SANS,
                      }}>
                        Market Information
                      </Typography>
                    </Box>
                    <StyledTableContainer component={Paper} elevation={0} sx={{ flex: 1, minHeight: 0, overflow: "auto", maxHeight: "none" }}>
                      <Table size="small">
                        <TableBody>
                          {[
                        {
                          label: "Shares Outstanding",
                          value:
                            Utils.format_number(
                              stockValues.compProfile.sharesOutstanding
                            ) || "N/A",
                        },
                        {
                          label: "Market Cap",
                          value: `${Utils.getCompanySize(
                            stockValues.compProfile.marketCap
                          )} (${Utils.format_currency_short(
                            stockValues.compProfile.marketCap
                          )})`,
                        },
                        {
                          label: "Beta",
                          value: stockValues.compProfile.beta
                            ? stockValues.compProfile.beta.toFixed(3)
                            : "N/A",
                        },
                        {
                          label: "Institution Ownership",
                          value: (() => {
                            return stockValues.compProfile.instOwnership
                              ? `${stockValues.compProfile.instOwnership.toFixed(
                                  2
                                )}%`
                              : "N/A";
                          })(),
                        },
                        {
                          label: "Insider Ownership",
                          value: (() => {
                            return stockValues.compProfile.insiderOwnerhip
                              ? `${stockValues.compProfile.insiderOwnerhip.toFixed(
                                  2
                                )}%`
                              : "N/A";
                          })(),
                        },
                        {
                          label: "Average Volume",
                          value: Utils.format_number(
                            stockValues.compProfile.avgVolume
                          ),
                        },
                      ].map((row, index) => (
                        <TableRow key={`market-${index}`}>
                          <TableCell
                            component="th"
                            scope="row"
                            sx={{ fontWeight: 600, fontSize: "0.875rem", color: C.textSecondary, fontFamily: SANS, px: 1.5 }}
                          >
                            {row.label}
                          </TableCell>
                          <TableCell 
                            align="right"
                            sx={{
                              fontSize: "1rem",
                              fontWeight: 700,
                              color: C.textPrimary,
                              fontFamily: MONO,
                              fontVariantNumeric: "tabular-nums",
                              whiteSpace: "nowrap",
                              px: 1.5,
                            }}
                          >
                            {row.value}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ backgroundColor: C.cardHeader }}>
                        <TableCell colSpan={2} sx={{ fontFamily: SANS, backgroundColor: C.cardHeader, color: C.cardHeaderText, fontWeight: 700, fontSize: "0.875rem", py: 0.5, px: 1.5, borderTop: `1px solid ${C.borderLight}`, borderBottom: `1px solid ${C.borderLight}` }}>Dividend</TableCell>
                      </TableRow>
                      {[
                        { label: "Dividend", value: Utils.format_currency_usd(stockValues.compProfile.lastDiv) || "$0.00" },
                        { label: "Dividend Yield", value: stockValues.compProfile.dividendYield ? `${stockValues.compProfile.dividendYield.toFixed(2)}%` : "0%" },
                      ].map((row, index) => (
                        <TableRow key={`dividend-${index}`}>
                          <TableCell component="th" scope="row" sx={{ fontWeight: 600, fontSize: "0.875rem", color: C.textSecondary, fontFamily: SANS, px: 1.5 }}>{row.label}</TableCell>
                          <TableCell align="right" sx={{ fontSize: "1rem", fontWeight: 700, color: C.textPrimary, fontFamily: MONO, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", px: 1.5 }}>{row.value}</TableCell>
                        </TableRow>
                      ))}
                        </TableBody>
                      </Table>
                    </StyledTableContainer>
                  </CardContent>
                </TableCard>
            </Box>
          </Grid>

          {/* Row 3: Price Chart + Price Performance (smaller) + Financial Metrics */}
          <Grid item xs={12} md={5} sx={{ display: "flex" }}>
            <PriceChartCard sx={{ width: "100%", height: "100%", "& .MuiCardContent-root": { p: 0 } }}>
              <CardContent sx={{ display: "flex", flexDirection: "column", "&:last-child": { pb: 0 } }}>
                <Box sx={{
                  display: "flex", alignItems: "center", gap: 1.25,
                  px: 1.5, py: 1,
                  borderBottom: `1px solid ${C.borderLight}`,
                  backgroundColor: C.cardHeader,
                }}>
                  <ShowChartIcon sx={{ fontSize: 20, color: C.navy }} />
                  <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: C.cardHeaderText, fontFamily: SANS }}>
                    Price Chart
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minHeight: 200, p: 1.5, display: "flex", justifyContent: "center", alignItems: "center" }}>
                  <StockChart symbol={stockValues.compProfile.symbol} />
                </Box>
              </CardContent>
            </PriceChartCard>
          </Grid>
          <Grid item xs={12} md={3} sx={{ display: "flex" }}>
            <TableCard sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
              <CardContent sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", p: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 1.5, py: 1, borderBottom: `1px solid ${C.borderLight}`, backgroundColor: C.cardHeader }}>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: C.cardHeaderText, fontFamily: SANS }}>Price Performance</Typography>
                </Box>
                <StyledTableContainer component={Paper} elevation={0} sx={{ flex: 1, minHeight: 0, overflow: "auto", maxHeight: "none" }}>
                  <Table size="small">
                    <TableBody>
                      {(() => {
                        const sr = stockValues.stockReport;
                        const perfRows = [
                          { label: "1 Day", raw: Utils.parsePerfNumber(sr.perfDay) },
                          { label: "1 Week", raw: Utils.parsePerfNumber(sr.perfWeek) },
                          { label: "1 Month", raw: Utils.parsePerfNumber(sr.perfMonth) },
                          { label: "YTD", raw: Utils.parsePerfNumber(sr.perfYTD) },
                          { label: "1 Year", raw: Utils.parsePerfNumber(sr.perfYear) },
                          { label: "2Y (Annualized)", raw: Utils.parsePerfNumber(sr.perf2Year) },
                          { label: "3Y (Annualized)", raw: Utils.parsePerfNumber(sr.perf3Year) },
                          { label: "5Y (Annualized)", raw: Utils.parsePerfNumber(sr.perf5Year) },
                          { label: "10Y (Annualized)", raw: Utils.parsePerfNumber(sr.perf10Year) },
                          { label: "5Y (Actual return)", raw: Utils.parsePerfNumber(sr.perf5YearTotal) },
                          { label: "10Y (Actual return)", raw: Utils.parsePerfNumber(sr.perf10YearTotal) },
                        ];
                        return perfRows.map((row, index) => {
                          const valueColor =
                            row.raw != null && row.raw < 0 ? C.danger : C.textPrimary;
                          return (
                            <TableRow key={index}>
                              <TableCell component="th" scope="row" sx={{ fontWeight: 600, fontSize: "0.875rem", color: C.textSecondary, fontFamily: SANS, px: 1.5 }}>{row.label}</TableCell>
                              <TableCell align="right" sx={{ fontSize: "1rem", fontWeight: 700, color: valueColor, fontFamily: MONO, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", px: 1.5 }}>{Utils.format_ratio(row.raw)}</TableCell>
                            </TableRow>
                          );
                        });
                      })()}
                    </TableBody>
                  </Table>
                </StyledTableContainer>
              </CardContent>
            </TableCard>
          </Grid>
          <Grid item xs={12} md={4} sx={{ display: "flex" }}>
            <TableCard sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
              <CardContent sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", p: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 1.5, py: 1, borderBottom: `1px solid ${C.borderLight}`, backgroundColor: C.cardHeader }}>
                  <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: C.cardHeaderText, fontFamily: SANS }}>Financial Metrics</Typography>
                </Box>
                <StyledTableContainer component={Paper} elevation={0} sx={{ flex: 1, minHeight: 0, overflow: "auto", maxHeight: "none" }}>
                  <Table size="small">
                    <TableBody>
                     
                      {[
                        {
                          label: "Sales Growth (5Y)",
                          value: this.props.stockValues.stockReport.salesGrowth,
                          threshold: 10,
                          isPercentage: true,
                        },
                        {
                          label: "Earnings Growth (5Y)",
                          value:
                            this.props.stockValues.stockReport.netIncomeGrowth,
                          threshold: 10,
                          isPercentage: true,
                        },
                        {
                          label: "Cashflow Growth (5Y)",
                          value:
                            this.props.stockValues.stockReport.cashflowGrowth,
                          threshold: 10,
                          isPercentage: true,
                        },
                        {
                          label: "FCF Growth (5Y)",
                          value: this.props.stockValues.stockReport?.fcfGrowth,
                          threshold: 10,
                          isPercentage: true,
                        },
                        {
                          label: "Future EPS Growth",
                          value: stockValues.compProfile.ePSnext5Y,
                          threshold: 10,
                          isPercentage: true,
                        },
                      ].map((row, index) => (
                        <TableRow key={`growth-${index}`}>
                          <TableCell
                            component="th"
                            scope="row"
                            sx={{ fontWeight: 600, fontSize: "0.875rem", color: C.textSecondary, fontFamily: SANS, px: 1.5 }}
                          >
                            {row.label}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: "1rem",
                              fontWeight: 700,
                              color: this.getValueColor(row),
                              fontFamily: MONO,
                              fontVariantNumeric: "tabular-nums",
                              whiteSpace: "nowrap",
                              px: 1.5,
                            }}
                          >
                            {row.value !== undefined && row.value !== null
                              ? `${Number(row.value).toFixed(2)}%`
                              : "N/A"}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ backgroundColor: C.cardHeader }}>
                        <TableCell colSpan={2} sx={{ fontFamily: SANS, backgroundColor: C.cardHeader, color: C.cardHeaderText, fontWeight: 700, fontSize: "0.875rem", py: 0.5, px: 1.5, borderTop: `1px solid ${C.borderLight}`, borderBottom: `1px solid ${C.borderLight}` }}>Performance Metrics</TableCell>
                      </TableRow>
                      {[
                        { label: "ROE", value: stockValues.compProfile.roe, threshold: 12, isPercentage: true },
                        { label: "Gross Margin", value: stockValues.compProfile.grossMargin, threshold: 50, isPercentage: true },
                        { label: "Profit Margin", value: stockValues.compProfile.profitMargin, threshold: 25, isPercentage: true },
                        { label: "ROIC", value: stockValues.stockReport?.roic, threshold: 10, isPercentage: true },
                      ].map((row, index) => (
                        <TableRow key={`perf-${index}`}>
                          <TableCell component="th" scope="row" sx={{ fontWeight: 600, fontSize: "0.875rem", color: C.textSecondary, fontFamily: SANS, px: 1.5 }}>{row.label}</TableCell>
                          <TableCell align="right" sx={{ fontSize: "1rem", fontWeight: 700, color: this.getValueColor(row), fontFamily: MONO, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", px: 1.5 }}>
                            {row.value ? `${Number(row.value).toFixed(2)}%` : "N/A"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </StyledTableContainer>
              </CardContent>
            </TableCard>
          </Grid>

          {/* Row 4: Valuation + ETF Exposure */}
          <Grid item xs={12} md={6} sx={{ display: "flex" }}>
            <TableCard sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
              <CardContent sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", p: 0 }}>
                <Box sx={{
                  display: "flex", alignItems: "center", gap: 1.25,
                  px: 1.5, py: 1,
                  borderBottom: `1px solid ${C.borderLight}`,
                  backgroundColor: C.cardHeader,
                }}>
                  <Typography sx={{
                    fontWeight: 700, fontSize: "1rem",
                    color: C.cardHeaderText, fontFamily: SANS,
                  }}>
                    Valuation
                  </Typography>
                </Box>
                <StyledTableContainer component={Paper} elevation={0} sx={{ flex: 1, minHeight: 0, overflow: "auto", maxHeight: "none" }}>
                  <Table size="small">
                    <TableBody>
                      {[
                        {
                          label: "EPS",
                          value: stockValues.compProfile.eps,
                          threshold: 0,
                          isPercentage: false,
                          format: "currency",
                        },
                        {
                          label: "PE Ratio",
                          value: stockValues.compProfile.pe,
                          threshold: 15,
                          isPercentage: false,
                          inverse: true,
                        },
                        {
                          label: "PEG Ratio",
                          value: stockValues.compProfile.pegRatio,
                          threshold: 1.5,
                          isPercentage: false,
                          inverse: true,
                        },
                        {
                          label: "P/S Ratio",
                          value: stockValues.compProfile.psRatio,
                          threshold: 0,
                          isPercentage: false,
                        },
                        {
                          label: "Book Value",
                          value: stockValues.compProfile.bookPerShare,
                          threshold: 0,
                          isPercentage: false,
                          format: "currency",
                        },
                        {
                          label: "P/B Ratio",
                          value: stockValues.compProfile.pbRatio,
                          threshold: 1,
                          isPercentage: false,
                          inverse: true,
                        },
                      ].map((row, index) => (
                        <TableRow key={`valuation-${index}`}>
                          <TableCell component="th" scope="row" sx={{ fontWeight: 600, fontSize: "0.875rem", color: C.textSecondary, fontFamily: SANS, px: 1.5 }}>{row.label}</TableCell>
                          <TableCell align="right" sx={{ fontSize: "1rem", fontWeight: 700, color: this.getValueColor(row), fontFamily: MONO, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", px: 1.5 }}>
                            {row.value !== undefined && row.value !== null ? row.format === "currency" ? Utils.format_currency_usd(row.value) : Number(row.value).toFixed(2) : "N/A"}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ backgroundColor: C.cardHeader }}>
                        <TableCell colSpan={2} sx={{ fontFamily: SANS, backgroundColor: C.cardHeader, color: C.cardHeaderText, fontWeight: 700, fontSize: "0.875rem", py: 0.5, px: 1.5, borderTop: `1px solid ${C.borderLight}`, borderBottom: `1px solid ${C.borderLight}` }}>Health</TableCell>
                      </TableRow>
                      {[
                        { label: "Current Ratio", value: stockValues.compProfile.currentRatio, threshold: 1, isPercentage: false },
                        { label: "Debt/Equity", value: stockValues.compProfile.debtEq, threshold: 1, isPercentage: false, inverse: true },
                        { label: "Debt Servicing", value: stockValues.compProfile.debtServicingRatio, threshold: 30, isPercentage: true, inverse: true },
                      ].map((row, index) => (
                        <TableRow key={`health-${index}`}>
                          <TableCell component="th" scope="row" sx={{ fontWeight: 600, fontSize: "0.875rem", color: C.textSecondary, fontFamily: SANS, px: 1.5 }}>{row.label}</TableCell>
                          <TableCell align="right" sx={{ fontSize: "1rem", fontWeight: 700, color: this.getValueColor(row), fontFamily: MONO, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", px: 1.5 }}>
                            {row.value ? (row.isPercentage ? `${Number(row.value).toFixed(2)}%` : Number(row.value).toFixed(2)) : "N/A"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </StyledTableContainer>
              </CardContent>
            </TableCard>
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: "flex" }}>
            <TableCard sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
              <CardContent sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", p: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 1.5, py: 1, borderBottom: `1px solid ${C.borderLight}`, backgroundColor: C.cardHeader }}>
                  <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: C.cardHeaderText, fontFamily: SANS }}>ETF Exposure</Typography>
                </Box>
                <StyledTableContainer component={Paper} elevation={0} sx={{ flex: 1, minHeight: 0, overflow: "auto", maxHeight: "none" }}>
                  <Table size="small">
                    <TableBody>
                      {stockValues.etfHoldings && stockValues.etfHoldings.slice(0, 10).map((etf) => (
                        <TableRow key={etf.etf} onClick={() => this.showModal(etf.etf, true)} sx={{ cursor: "pointer" }}>
                          <TableCell sx={{ px: 1.5 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Box component="img" src={`${Constants.IMAGE_URL}${etf.etf}.jpg`} onError={(e) => { e.target.style.display = "none"; }} sx={{ width: 24, height: 24, objectFit: "contain", borderRadius: "4px" }} />
                              <Typography variant="body2" sx={{ fontSize: "0.875rem", fontFamily: SANS }}>{etf.etf}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: "1rem", fontWeight: 700, color: C.textPrimary, fontFamily: MONO, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", px: 1.5 }}>
                            {etf.weightPercentage === 10000 ? "N/A" : `${etf.weightPercentage}%`}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </StyledTableContainer>
              </CardContent>
            </TableCard>
          </Grid>

          {/* Row 5: News and Stock Grade */}
          <Grid item xs={12} md={6} sx={{ display: "flex" }}>
            <NewsCard sx={{ width: "100%", height: "100%" }}>
              <CardContent sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                  <CompanyNews symbol={stockValues.symbol} />
                </Box>
              </CardContent>
            </NewsCard>
          </Grid>

          <Grid item xs={12} md={6} sx={{ display: "flex" }}>
            <GradeCard sx={{ width: "100%", height: "100%" }}>
              <CardContent sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                  <StockGrade symbol={stockValues.compProfile.symbol} />
                </Box>
              </CardContent>
            </GradeCard>
          </Grid>
        </Grid>

        <Modal
          open={this.state.open}
          onClose={() => this.setOpen(false)}
          aria-labelledby="company-page-modal"
          aria-describedby="company-page-modal-description"
          sx={{
            "& .MuiDialog-paper": {
              margin: 0,
              maxWidth: "100%",
              width: "100%",
              height: "100%",
              maxHeight: "100%",
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            },
          }}
        >
          <Box
            sx={{
              p: 0,
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ flex: 1, overflow: "auto" }}>
              <CompanyPage symbol={this.state.modalSymbol} />
            </Box>
            <Box sx={{ p: 1, display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => this.setOpen(false)}
              >
                Close
              </Button>
            </Box>
          </Box>
        </Modal>
      </Container>

      {/* Scroll hint: clickable icon in bottom-right corner, scrolls down on click (portal so it's never clipped) */}
      {typeof document !== "undefined" &&
        this.state.showScrollHint &&
        ReactDOM.createPortal(
          <Box
            component="button"
            type="button"
            onClick={() => {
              window.scrollTo({ top: window.scrollY + window.innerHeight * 0.8, behavior: "smooth" });
            }}
            sx={{
              position: "fixed",
              bottom: 20,
              right: 20,
              zIndex: 10000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: C.cardHeader,
              border: `1px solid ${C.border}`,
              boxShadow: "0 2px 8px rgba(15,45,82,0.12)",
              color: C.navy,
              cursor: "pointer",
              padding: 0,
              "&:hover": {
                backgroundColor: C.navyLight,
              },
            }}
            className="scroll-hint-arrow"
            aria-label="Scroll down for more content"
          >
            <KeyboardArrowDownIcon sx={{ fontSize: 26 }} />
          </Box>,
          document.body
        )}
      </>
    );
  };
}
